export type ExportFormat = 'csv' | 'jsonl' | 'json';

export function buildAdminExportUrl(params: {
  baseUrl: string;
  siteKey: string;
  fromIso?: string;
  toIso?: string;
  range?: string; // e.g., '7d', '30d', '24h'
  format: ExportFormat;
}): string {
  const { baseUrl, siteKey, fromIso, toIso, range, format } = params;
  const u = new URL(
    `/admin/analytics/sites/by-key/${encodeURIComponent(siteKey)}/consents/export`,
    baseUrl,
  );
  u.searchParams.set('format', format);
  if (fromIso) u.searchParams.set('from', fromIso);
  if (toIso) u.searchParams.set('to', toIso);
  if (!fromIso && range) u.searchParams.set('range', range);
  return u.toString();
}

export async function downloadAdminExport(params: {
  baseUrl: string;
  siteKey: string;
  fromIso: string;
  toIso: string;
  format: ExportFormat;
  authToken?: string;
}): Promise<void> {
  const url = buildAdminExportUrl({
    baseUrl: params.baseUrl,
    siteKey: params.siteKey,
    fromIso: params.fromIso,
    toIso: params.toIso,
    format: params.format,
  });
  const headers: Record<string, string> = {};
  if (params.authToken) headers['Authorization'] = `Bearer ${params.authToken}`;
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const datePart = `${params.fromIso.slice(0, 10)}_${params.toIso.slice(0, 10)}`;
  a.download = `consents_${params.siteKey}_${datePart}.${params.format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export function buildAdminReceiptsExportUrl(params: {
  baseUrl: string;
  siteKey: string;
  fromIso: string;
  toIso: string;
  format: 'jsonl' | 'json';
}): string {
  const { baseUrl, siteKey, fromIso, toIso, format } = params;
  const u = new URL('/admin/receipts/export', baseUrl);
  u.searchParams.set('siteId', siteKey);
  u.searchParams.set('from', fromIso);
  u.searchParams.set('to', toIso);
  u.searchParams.set('format', format);
  return u.toString();
}

export async function downloadAdminReceiptsExport(params: {
  baseUrl: string;
  siteKey: string;
  fromIso: string;
  toIso: string;
  format: 'jsonl' | 'json';
  authToken?: string;
}): Promise<void> {
  const url = buildAdminReceiptsExportUrl(params);
  const headers: Record<string, string> = {};
  if (params.authToken) headers['Authorization'] = `Bearer ${params.authToken}`;
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`Receipts export failed: ${res.status}`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const datePart = `${params.fromIso.slice(0, 10)}_${params.toIso.slice(0, 10)}`;
  a.download = `receipts_${params.siteKey}_${datePart}.${params.format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

// Paged CSV export for consents (guardrailed)
export function buildAdminConsentsCsvPageUrl(params: {
  baseUrl: string;
  siteId: string; // accepts siteId or siteKey depending on server; we send siteId value
  fromIso?: string;
  toIso?: string;
  page?: number;
  pageSize?: number;
}): string {
  const { baseUrl, siteId, fromIso, toIso, page = 1, pageSize = 5000 } = params;
  const u = new URL('/api/admin/consents/export.csv', baseUrl);
  u.searchParams.set('siteId', siteId);
  if (fromIso) u.searchParams.set('from', fromIso);
  if (toIso) u.searchParams.set('to', toIso);
  u.searchParams.set('page', String(page));
  u.searchParams.set('pagesize', String(pageSize));
  return u.toString();
}

export async function downloadAdminConsentsCsvPage(params: {
  baseUrl: string;
  siteId: string;
  fromIso?: string;
  toIso?: string;
  page?: number;
  pageSize?: number;
  authToken?: string;
}): Promise<void> {
  const url = buildAdminConsentsCsvPageUrl(params);
  const headers: Record<string, string> = {};
  if (params.authToken) headers['Authorization'] = `Bearer ${params.authToken}`;
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`CSV export failed: ${res.status}`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const datePart = `${(params.fromIso ?? '').slice(0, 10)}_${(params.toIso ?? '').slice(0, 10)}`;
  a.download = `consents_p${params.page ?? 1}_${datePart}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}
